import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import { AdminDriverSummary, acceptRate } from '@app/Drivers/types';
import { getDrivers } from '@app/Drivers/drivers.api';
import { Pill } from '@app/Campaigns/badges';
import { colors, radii, shadows } from '@app/components/designTokens';

type AvailFilter = 'all' | 'available' | 'unavailable';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  color: colors.subtle,
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: 'nowrap'
};
const td: React.CSSProperties = {
  padding: '13px 16px',
  fontSize: 14,
  color: colors.body,
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle'
};

const km = (m: number) => `${Math.round(m / 1000)} km`;

export const Drivers: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<AdminDriverSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [avail, setAvail] = useState<AvailFilter>('all');
  const [hovered, setHovered] = useState<number | null>(null);

  const load = useCallback(
    async (spinner: boolean) => {
      if (spinner) setLoading(true);
      try {
        const availableParam = avail === 'all' ? undefined : avail === 'available';
        const data = await getDrivers({ q: search || undefined, available: availableParam, limit: 200 });
        setDrivers(data);
        setError(null);
      } catch (e: any) {
        setError(e?.message || 'Failed to load drivers');
        if (!spinner) toast.error('Could not refresh drivers');
      } finally {
        if (spinner) setLoading(false);
      }
    },
    [search, avail]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 700, color: colors.title, margin: 0 },
    subtle: { fontSize: 13, color: colors.subtle },
    refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: `1px solid ${colors.border}`, borderRadius: radii.button, background: '#fff', color: colors.body, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    toolbar: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
    search: { flex: 1, minWidth: 220, maxWidth: 340, padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, outline: 'none' },
    select: { padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, background: '#fff', color: colors.body },
    card: { background: '#fff', borderRadius: radii.card, boxShadow: shadows.softCard, border: `1px solid ${colors.border}`, overflow: 'hidden' },
    metric: { fontWeight: 600 },
    metricSub: { fontSize: 12, color: colors.subtle }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Drivers</h1>
          <div style={styles.subtle}>{loading ? 'Loading…' : `${drivers.length} drivers loaded`}</div>
        </div>
        <button style={styles.refreshBtn} onClick={() => load(true)}>
          <SyncIcon className={loading ? 'logo-bolt-pulse' : undefined} /> Refresh
        </button>
      </div>

      <div style={styles.toolbar}>
        <input style={styles.search} placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} />
        <select style={styles.select} value={avail} onChange={e => setAvail(e.target.value as AvailFilter)}>
          <option value="all">All drivers</option>
          <option value="available">Available</option>
          <option value="unavailable">Unavailable</option>
        </select>
      </div>

      {error && (
        <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 12 }}>{error}</div>
      )}

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>Loading drivers…</div>
        ) : drivers.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>No drivers match these filters.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
              <thead>
                <tr>
                  <th style={th}>Driver</th>
                  <th style={th}>Available</th>
                  <th style={th}>Paired screen</th>
                  <th style={th}>Runs</th>
                  <th style={th}>Accept rate</th>
                  <th style={th}>Distance</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map(d => {
                  const rate = acceptRate(d);
                  return (
                    <tr
                      key={d.driverProfileId}
                      onClick={() => navigate(`/drivers/${d.driverProfileId}`)}
                      onMouseEnter={() => setHovered(d.driverProfileId)}
                      onMouseLeave={() => setHovered(null)}
                      style={{ cursor: 'pointer', background: hovered === d.driverProfileId ? '#f8fafc' : 'transparent' }}
                    >
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{d.name || `Driver #${d.driverProfileId}`}</div>
                        <div style={{ fontSize: 12, color: colors.subtle }}>{d.email}</div>
                      </td>
                      <td style={td}>
                        {d.available ? <Pill text="AVAILABLE" tone="green" /> : <Pill text="UNAVAILABLE" tone="gray" />}
                      </td>
                      <td style={td}>
                        {d.pairedScreenName ? (
                          d.pairedScreenName
                        ) : (
                          <span style={{ color: colors.placeholder }}>not paired</span>
                        )}
                      </td>
                      <td style={td}>
                        <span style={styles.metric}>{d.runsTotal}</span>
                        <div style={styles.metricSub}>
                          {d.runsCompleted} done · {d.runsInProgress} live
                        </div>
                      </td>
                      <td style={td}>
                        {rate == null ? (
                          <span style={{ color: colors.placeholder }}>—</span>
                        ) : (
                          <>
                            <span style={styles.metric}>{rate}%</span>
                            <div style={styles.metricSub}>
                              {d.runsApproved} ok · {d.runsDeclined} declined
                            </div>
                          </>
                        )}
                      </td>
                      <td style={td}>{km(d.distanceMeters)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
